export interface FloatingPositionOptions {
  /** The inspector element to base horizontal placement on */
  inspectorEl: HTMLElement;
  /** Anchor element (row) whose top we try to align to */
  anchorEl?: HTMLElement | null;
  /** Panel dimensions (approximate / known) */
  width: number;
  height: number;
  /** Gap between inspector and panel */
  gap?: number;
  /** Minimum viewport padding */
  padding?: number;
  /** Preferred side if space allows */
  preferSide?: 'left' | 'right';
}

export interface FloatingPositionResult {
  top: number;
  left: number;
  side: 'left' | 'right';
}

/**
 * Computes a static floating panel position beside an inspector.
 * Decides left/right based on available space (or preferSide if it fits) and clamps vertically.
 */
export function computeFloatingPosition(
  opts: FloatingPositionOptions,
): FloatingPositionResult | null {
  const { inspectorEl, anchorEl, width, height, gap = 8, padding = 8, preferSide } = opts;
  if (!inspectorEl) return null;
  const inspectorRect = inspectorEl.getBoundingClientRect();
  const anchorRect = anchorEl?.getBoundingClientRect();
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  const spaceLeft = inspectorRect.left - gap - width - padding;
  const spaceRight = viewportW - (inspectorRect.right + gap + width + padding);
  let side: 'left' | 'right';
  if (preferSide === 'left' && spaceLeft >= 0) side = 'left';
  else if (preferSide === 'right' && spaceRight >= 0) side = 'right';
  else
    side =
      spaceLeft >= spaceRight
        ? spaceLeft >= 0
          ? 'left'
          : 'right'
        : spaceRight >= 0
          ? 'right'
          : 'left';

  const left = side === 'left' ? inspectorRect.left - width - gap : inspectorRect.right + gap;

  const desiredTop = anchorRect ? anchorRect.top : inspectorRect.top;
  const maxTop = Math.max(padding, viewportH - height - padding);
  const top = Math.min(Math.max(padding, desiredTop), maxTop);
  return { top, left, side };
}
